// src/Components/DocumentScanner/DocumentScanner.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import Webcam from 'react-webcam';
import Cropper from 'react-cropper';
// Cropper CSS loaded from CDN in public/index.html
import { jsPDF } from 'jspdf';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Slider,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Cameraswitch as SwitchCameraIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Contrast as ContrastIcon,
  Brightness5 as BrightnessIcon,
  Check as CheckIcon,
  Refresh as RetakeIcon,
  AddPhotoAlternate as AddPageIcon,
  PictureAsPdf as PdfIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { documentService } from '../../services/documentService';
import logger from '../../utils/logger';

const STEPS = ['Capture', 'Adjust', 'Review'];

const DocumentScanner = ({
  onComplete,
  onCancel,
  destinationPath = '/',
  destinationType = 'personal', // 'personal' | 'family' | 'vault'
  storageStrategy = 'fileExplorer', // 'fileExplorer' | 'documentService'
}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const webcamRef = useRef(null);
  const cropperRef = useRef(null);

  // State
  const [activeStep, setActiveStep] = useState(0);
  const [capturedImage, setCapturedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [scannedPages, setScannedPages] = useState([]);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' or 'environment'
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [error, setError] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileBaseName, setFileBaseName] = useState('');
  const [selectedPath, setSelectedPath] = useState(destinationPath);
  const [selectedDestinationType, setSelectedDestinationType] = useState(destinationType);
  const [saveType, setSaveType] = useState('pdf');

  // Video constraints for webcam
  const videoConstraints = {
    facingMode: facingMode,
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  };

  useEffect(() => {
    setSelectedPath(destinationPath || '/');
    setSelectedDestinationType(destinationType);
  }, [destinationPath, destinationType]);

  useEffect(() => {
    if (activeStep === 2 && scannedPages.length > 0 && !fileBaseName) {
      setFileBaseName(`scan_${Date.now()}`);
    }
  }, [activeStep, scannedPages.length, fileBaseName]);

  // Capture image from webcam
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImage(imageSrc);
        setActiveStep(1);
        setError(null);
      } else {
        setError('Failed to capture image. Please try again.');
      }
    }
  }, []);

  // Switch between front and back camera
  const switchCamera = useCallback(() => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  }, []);

  // Handle camera errors
  const handleCameraError = useCallback((err) => {
    logger.error('Camera error:', err);
    setCameraError('Unable to access camera. Please ensure camera permissions are granted.');
  }, []);

  // Crop the image
  const handleCrop = useCallback(() => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      const croppedCanvas = cropper.getCroppedCanvas({
        maxWidth: 2048,
        maxHeight: 2048,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (croppedCanvas) {
        // Apply brightness and contrast filters
        const ctx = croppedCanvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, croppedCanvas.width, croppedCanvas.height);
        const data = imageData.data;

        const brightnessValue = (brightness - 100) * 2.55;
        const contrastFactor = (contrast / 100);

        for (let i = 0; i < data.length; i += 4) {
          // Apply brightness
          data[i] = Math.min(255, Math.max(0, data[i] + brightnessValue));
          data[i + 1] = Math.min(255, Math.max(0, data[i + 1] + brightnessValue));
          data[i + 2] = Math.min(255, Math.max(0, data[i + 2] + brightnessValue));

          // Apply contrast
          data[i] = Math.min(255, Math.max(0, ((data[i] - 128) * contrastFactor) + 128));
          data[i + 1] = Math.min(255, Math.max(0, ((data[i + 1] - 128) * contrastFactor) + 128));
          data[i + 2] = Math.min(255, Math.max(0, ((data[i + 2] - 128) * contrastFactor) + 128));
        }

        ctx.putImageData(imageData, 0, 0);

        const processedImage = croppedCanvas.toDataURL('image/jpeg', 0.92);
        setCroppedImage(processedImage);
      }
    }
  }, [brightness, contrast]);

  // Add current page to scanned pages
  const addPage = useCallback(() => {
    if (croppedImage) {
      setScannedPages(prev => [...prev, croppedImage]);
      setCapturedImage(null);
      setCroppedImage(null);
      setBrightness(100);
      setContrast(100);
      setActiveStep(0);
    }
  }, [croppedImage]);

  // Confirm single page and move to review
  const confirmPage = useCallback(() => {
    if (croppedImage) {
      setScannedPages(prev => [...prev, croppedImage]);
      setActiveStep(2);
    }
  }, [croppedImage]);

  // Retake current photo
  const retake = useCallback(() => {
    setCapturedImage(null);
    setCroppedImage(null);
    setBrightness(100);
    setContrast(100);
    setActiveStep(0);
  }, []);

  // Remove a page from scanned pages
  const removePage = useCallback((index) => {
    setScannedPages(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Rotate image
  const rotateImage = useCallback((direction) => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      cropper.rotate(direction === 'left' ? -90 : 90);
    }
  }, []);

  // Generate PDF from scanned pages
  const generatePDF = useCallback(async () => {
    if (scannedPages.length === 0) return null;

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < scannedPages.length; i++) {
      if (i > 0) {
        pdf.addPage();
      }

      const img = new Image();
      img.src = scannedPages[i];

      await new Promise((resolve) => {
        img.onload = () => {
          const imgRatio = img.width / img.height;
          const pageRatio = pageWidth / pageHeight;

          let imgWidth, imgHeight;

          if (imgRatio > pageRatio) {
            imgWidth = pageWidth - 10;
            imgHeight = imgWidth / imgRatio;
          } else {
            imgHeight = pageHeight - 10;
            imgWidth = imgHeight * imgRatio;
          }

          const x = (pageWidth - imgWidth) / 2;
          const y = (pageHeight - imgHeight) / 2;

          pdf.addImage(scannedPages[i], 'JPEG', x, y, imgWidth, imgHeight);
          resolve();
        };
      });
    }

    return pdf;
  }, [scannedPages]);

  // Upload to Firebase
  const uploadDocument = useCallback(async () => {
    if (scannedPages.length === 0 || !profile?.familyCode) return;

    setUploading(true);
    setError(null);

    try {
      const trimmedBaseName = fileBaseName.trim();
      if (!trimmedBaseName) {
        throw new Error('Please enter a file name');
      }

      const saveTypeConfig = {
        pdf: { extension: 'pdf', mimeType: 'application/pdf' },
      };
      const selectedSaveType = saveTypeConfig[saveType] || saveTypeConfig.pdf;

      const normalizedPath = selectedPath && selectedPath !== '/'
        ? selectedPath.replace(/^\/+|\/+$/g, '')
        : '';
      const savedPath = normalizedPath ? `/${normalizedPath}` : '/';

      // Generate PDF
      setUploadProgress(10);
      const pdf = await generatePDF();
      if (!pdf) throw new Error('Failed to generate PDF');

      setUploadProgress(30);

      // Convert PDF to blob
      const pdfBlob = pdf.output('blob');

      // Generate filename
      const fileName = `${trimmedBaseName}.${selectedSaveType.extension}`;

      if (storageStrategy === 'documentService') {
        if (!currentUser?.uid) {
          throw new Error('User is not authenticated');
        }

        if (selectedDestinationType === 'family') {
          throw new Error('Family documents are not available in this scanner');
        }

        const folderPath = selectedPath?.trim() || '/';
        const normalizedFolderPath = folderPath.startsWith('/') ? folderPath : `/${folderPath}`;
        const pdfFile = new File([pdfBlob], fileName, { type: selectedSaveType.mimeType });

        setUploadProgress(50);

        const progressMapper = (progress) => {
          setUploadProgress(50 + Math.round(progress * 0.4));
        };

        const uploadResult = selectedDestinationType === 'vault'
          ? await documentService.uploadVaultDocument(
              profile.familyCode,
              currentUser.uid,
              pdfFile,
              normalizedFolderPath,
              progressMapper
            )
          : await documentService.uploadPersonalDocument(
              profile.familyCode,
              currentUser.uid,
              pdfFile,
              normalizedFolderPath,
              progressMapper
            );

        setUploadProgress(95);

        if (onComplete) {
          onComplete({
            fileName: uploadResult?.fileName || fileName,
            downloadURL: uploadResult?.fileUrl,
            pageCount: scannedPages.length,
          });
        }

        setUploadProgress(100);
        return;
      }

      // Determine storage path based on destination type
      let storagePath;
      let firestorePath;
      const pathSuffix = normalizedPath ? `${normalizedPath}/` : '';

      switch (selectedDestinationType) {
        case 'vault':
          storagePath = `families/${profile.familyCode}/vault/${currentUser.uid}/${pathSuffix}${fileName}`;
          firestorePath = `families/${profile.familyCode}/vault`;
          break;
        case 'family':
          storagePath = `families/${profile.familyCode}/documents/${pathSuffix}${fileName}`;
          firestorePath = `families/${profile.familyCode}/documents`;
          break;
        case 'personal':
        default:
          storagePath = `users/${currentUser.uid}/documents/${pathSuffix}${fileName}`;
          firestorePath = `users/${currentUser.uid}/documents`;
          break;
      }

      setUploadProgress(50);

      // Upload to Firebase Storage
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, pdfBlob);

      setUploadProgress(70);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      setUploadProgress(85);

      // Save metadata to Firestore
      const docData = {
        name: fileName,
        fileName: fileName,
        fileUrl: downloadURL,
        fileType: selectedSaveType.mimeType,
        type: selectedSaveType.mimeType,
        fileSize: pdfBlob.size,
        path: savedPath,
        uploadedBy: currentUser.uid,
        uploadedByName: profile?.displayName || currentUser.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isDirectory: false,
        scanSource: 'web-scanner',
        pageCount: scannedPages.length,
        // For AI processing later
        processedByAI: false,
        status: 'pending',
      };

      await addDoc(collection(db, firestorePath), docData);

      setUploadProgress(100);

      // Success callback
      if (onComplete) {
        onComplete({
          fileName,
          downloadURL,
          pageCount: scannedPages.length,
        });
      }

    } catch (err) {
      logger.error('Upload error:', err);
      setError(`Failed to upload document: ${err.message}`);
    } finally {
      setUploading(false);
    }
  }, [
    scannedPages,
    profile,
    currentUser,
    generatePDF,
    onComplete,
    fileBaseName,
    selectedPath,
    selectedDestinationType,
    saveType,
    storageStrategy,
  ]);

  // Render capture step
  const renderCaptureStep = () => (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      {cameraError ? (
        <Paper sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{cameraError}</Alert>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Paper>
      ) : (
        <>
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.92}
            videoConstraints={videoConstraints}
            onUserMediaError={handleCameraError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />

          {/* Camera overlay guide */}
          <Box
            sx={{
              position: 'absolute',
              top: '10%',
              left: '5%',
              right: '5%',
              bottom: '15%',
              border: '2px dashed rgba(255,255,255,0.5)',
              borderRadius: 2,
              pointerEvents: 'none',
            }}
          />

          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              px: 2,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            Position document within the frame
          </Typography>

          {/* Camera controls */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 0,
              right: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Tooltip title="Switch Camera">
              <IconButton
                onClick={switchCamera}
                sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
              >
                <SwitchCameraIcon />
              </IconButton>
            </Tooltip>

            <IconButton
              onClick={captureImage}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 64,
                height: 64,
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <CameraIcon sx={{ fontSize: 32 }} />
            </IconButton>

            {scannedPages.length > 0 && (
              <Tooltip title={`${scannedPages.length} page(s) scanned`}>
                <IconButton
                  onClick={() => setActiveStep(2)}
                  sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                >
                  <PdfIcon />
                  <Typography variant="caption" sx={{ position: 'absolute', top: -5, right: -5, bgcolor: 'error.main', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {scannedPages.length}
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  // Render adjust step
  const renderAdjustStep = () => (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ position: 'relative', width: '100%', height: 350, bgcolor: 'grey.900', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
        <Cropper
          ref={cropperRef}
          src={capturedImage}
          style={{ height: '100%', width: '100%' }}
          aspectRatio={NaN}
          guides={true}
          viewMode={1}
          dragMode="move"
          autoCropArea={0.9}
          restore={false}
          modal={true}
          highlight={true}
          cropBoxMovable={true}
          cropBoxResizable={true}
          toggleDragModeOnDblclick={false}
        />
      </Box>

      {/* Adjustment controls */}
      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Brightness */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <BrightnessIcon fontSize="small" />
              <Typography variant="body2">Brightness: {brightness}%</Typography>
            </Box>
            <Slider
              value={brightness}
              onChange={(_, value) => setBrightness(value)}
              min={50}
              max={150}
              valueLabelDisplay="auto"
            />
          </Box>

          {/* Contrast */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ContrastIcon fontSize="small" />
              <Typography variant="body2">Contrast: {contrast}%</Typography>
            </Box>
            <Slider
              value={contrast}
              onChange={(_, value) => setContrast(value)}
              min={50}
              max={150}
              valueLabelDisplay="auto"
            />
          </Box>

          {/* Rotation */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Tooltip title="Rotate Left">
              <IconButton onClick={() => rotateImage('left')}>
                <RotateLeftIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Rotate Right">
              <IconButton onClick={() => rotateImage('right')}>
                <RotateRightIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button startIcon={<RetakeIcon />} onClick={retake}>
          Retake
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AddPageIcon />}
            onClick={() => { handleCrop(); setTimeout(addPage, 100); }}
          >
            Add Page
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => { handleCrop(); setTimeout(confirmPage, 100); }}
          >
            Done
          </Button>
        </Box>
      </Box>
    </Box>
  );

  // Render review step
  const renderReviewStep = () => (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Review Scanned Pages ({scannedPages.length})
      </Typography>

      {scannedPages.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No pages scanned yet</Typography>
          <Button
            variant="contained"
            startIcon={<CameraIcon />}
            onClick={() => setActiveStep(0)}
            sx={{ mt: 2 }}
          >
            Scan a Page
          </Button>
        </Paper>
      ) : (
        <>
          {/* Page thumbnails */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            {scannedPages.map((page, index) => (
              <Paper
                key={index}
                elevation={2}
                sx={{
                  position: 'relative',
                  width: 120,
                  height: 160,
                  overflow: 'hidden',
                }}
              >
                <img
                  src={page}
                  alt={`Page ${index + 1}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    p: 0.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="caption">Page {index + 1}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => removePage(index)}
                    sx={{ color: 'white', p: 0.25 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>
            ))}

            {/* Add more pages button */}
            <Paper
              elevation={1}
              sx={{
                width: 120,
                height: 160,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                border: '2px dashed',
                borderColor: 'divider',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={() => setActiveStep(0)}
            >
              <AddPageIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Add Page
              </Typography>
            </Paper>
          </Box>

          {/* Upload progress */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Save options
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="File name"
                value={fileBaseName}
                onChange={(event) => setFileBaseName(event.target.value)}
                helperText="The .pdf extension is added automatically."
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel id="scan-save-type-label">Save type</InputLabel>
                <Select
                  labelId="scan-save-type-label"
                  value={saveType}
                  label="Save type"
                  onChange={(event) => setSaveType(event.target.value)}
                >
                  <MenuItem value="pdf">PDF (.pdf)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="scan-location-label">Location</InputLabel>
                <Select
                  labelId="scan-location-label"
                  value={selectedDestinationType}
                  label="Location"
                  onChange={(event) => setSelectedDestinationType(event.target.value)}
                >
                  <MenuItem value="personal">Personal Documents</MenuItem>
                  {storageStrategy !== 'documentService' && (
                    <MenuItem value="family">Family Documents</MenuItem>
                  )}
                  <MenuItem value="vault">Vault</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Folder path"
                value={selectedPath}
                onChange={(event) => setSelectedPath(event.target.value)}
                helperText="Use / for the root folder."
                fullWidth
              />
            </Stack>
          </Paper>
          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Uploading... {uploadProgress}%
              </Typography>
              <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                <Box
                  sx={{
                    width: `${uploadProgress}%`,
                    bgcolor: 'primary.main',
                    borderRadius: 1,
                    height: '100%',
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
              onClick={uploadDocument}
              disabled={uploading || scannedPages.length === 0}
            >
              {uploading ? 'Uploading...' : `Upload ${scannedPages.length} Page(s)`}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Stepper */}
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Step content */}
      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && renderCaptureStep()}
        {activeStep === 1 && renderAdjustStep()}
        {activeStep === 2 && renderReviewStep()}
      </Box>
    </Box>
  );
};

export default DocumentScanner;
